import React, { useState, useEffect } from 'react';
import {
  DarkPopup,
  DarkButton,
  Icon,
  DarkText,
  ActivityIndicator,
  TooltipPopup,
  SnackBarHelper,
  Loader
} from '@lyearn-team/frontend-components';
import { saveAs } from 'file-saver';
import themeConfig from '@lyearn-team/theme-config';
import moment from 'moment';
import c from 'classnames';
import CopyToClipboard from 'react-copy-to-clipboard';
import memoize from 'lodash/memoize';
import AnimateSliderBottom from '../../../../views/DarkHomePage/model-views/AnimateSliderBottom';
import { badgeRequest } from '../../../../../components/PreviewContentIndex/helper/requests';
import { previewContainer } from './DarkBadgePopup.style';

const fetchBadge = memoize(async badgeId => {
  const result = await badgeRequest(badgeId);
  const badgeDetails = await result.json();
  return badgeDetails;
});

const showSnackbar = (label, status = 'error') => {
  SnackBarHelper.showSnackbar({
    label,
    duration: 7,
    status
  });
};

const renderClose = (changeActiveTab, callback) => (
  <DarkButton.IconButton
    onClick={() => {
      changeActiveTab('none');
      callback && callback();
    }}
    className="closeButton"
  >
    <Icon type="close" className="icon" />
  </DarkButton.IconButton>
);

const redirectToBadge = url => {
  window.open(url, '_blank');
};

const linkedInPreFilledURL = ({ badgeName, fromUnformatted, url, number }) => {
  const certificateUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURI(
    badgeName
  )}&issueYear=${new Date(fromUnformatted).getFullYear()}&issueMonth=${new Date(
    fromUnformatted
  ).getMonth()}&certUrl=${encodeURIComponent(url)}&certId=${number}`;

  return certificateUrl;
};

const redirectToLinkedin = badge => () => {
  const url = linkedInPreFilledURL(badge);
  window.open(url, '_blank');
};

const renderShareTab = ({ link, isMobile, changeActiveTab, copied, setCopied }) => {
  return (
    <div className="tabInnerWrapper">
      {renderClose(changeActiveTab, () => setCopied(false))}
      <DarkText
        className="tabInnerHeader"
        as="div"
        fontSize={isMobile ? 32 : 18}
        color="silver"
        letterSpacing="normal"
      >
        Share Badge
      </DarkText>
      <DarkText
        className="tabInnerInstruction"
        as="div"
        fontSize={14}
        color="copper"
        letterSpacing="normal"
      >
        Copy the link below to share your credential with your professional network.
      </DarkText>
      <div className="shareBadgeLink">
        <div className="darkLinkStyle">
          <p className="url">{link}</p>
          <div className="copyButtonWrapper">
            <div className="copyButtonSeperator" />
            <CopyToClipboard text={link} onCopy={() => setCopied(true)}>
              <DarkButton.TextButton onClick={() => {}} className="copyButton">
                {copied ? 'Copied' : 'Copy'}
              </DarkButton.TextButton>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    </div>
  );
};

const renderLinkedinTab = (badge, isMobile, changeActiveTab) => {
  const fields = [
    {
      text: 'Badge Name:',
      key: 'badgeName',
      showCopy: true
    },
    {
      text: 'Badge authority:',
      key: 'company',
      showCopy: true
    },
    {
      text: 'License number:',
      key: 'number',
      showCopy: true
    },
    {
      text: 'Issue Date:',
      key: 'from'
    },
    {
      text: 'Expiration Date:',
      key: 'to'
    },
    {
      text: 'Badge URL:',
      key: 'url',
      showCopy: true
    }
  ];
  return (
    <div className="tabInnerWrapper">
      {renderClose(changeActiveTab)}
      <DarkText
        className="tabInnerHeader"
        as="div"
        fontSize={18}
        color="silver"
        letterSpacing="normal"
      >
        Add to LinkedIn
      </DarkText>
      <DarkText
        className="tabInnerInstruction"
        as="div"
        fontSize={isMobile ? 16 : 14}
        color={isMobile ? 'silver' : 'copper'}
        letterSpacing="normal"
      >
        Add this to your LinkedIn account under the user profile section and let your professional
        network know about this awarded credential.
      </DarkText>
      {fields.map(field => (
        <div className="tabInnerDetails">
          <DarkText
            className="tabInnerDetailsContent key"
            fontSize={14}
            color="iron"
            letterSpacing="normal"
          >
            {field.text}
          </DarkText>
          <DarkText
            className="tabInnerDetailsContent"
            fontSize={14}
            color="copper"
            letterSpacing="normal"
          >
            {field.text.includes('URL') ? (
              <DarkButton.TextButton
                onClick={() => redirectToBadge(badge[field.key])}
                style={{ textAlign: 'left' }}
              >
                <span className="url">{badge[field.key]}</span>
              </DarkButton.TextButton>
            ) : (
              badge[field.key]
            )}
            {field.showCopy ? (
              <CopyToClipboard text={badge[field.key]}>
                <div className="copyIconContainer">
                  {!isMobile ? (
                    <TooltipPopup
                      title={
                        <DarkText className="text" as="div" fontSize={12} letterSpacing="normal">
                          Copy to Clipboard
                        </DarkText>
                      }
                      placement={field.text.includes('URL') ? 'left' : 'right'}
                      trigger="hover"
                      showCross={false}
                      hideArrow={true}
                      className="copyToClipboard"
                      leftOffset={field.text.includes('URL') ? -10 : 10}
                    >
                      <Icon type="duplicate" className="copyIcon" />
                    </TooltipPopup>
                  ) : (
                    <Icon type="duplicate" className="copyIcon" />
                  )}
                </div>
              </CopyToClipboard>
            ) : null}
          </DarkText>
        </div>
      ))}

      <DarkButton.SecondaryButton
        className="goToLinkedIn"
        onClick={redirectToLinkedin(badge)}
        size={isMobile ? 'medium' : 'small'}
        uppercase={true}
      >
        {isMobile ? '' : 'Go to Linkedin'}
        <Icon type="globe" className="icon" />
        {isMobile ? 'Go to Linkedin' : ''}
      </DarkButton.SecondaryButton>
    </div>
  );
};

const renderOpenTab = ({
  active,
  changeActiveTab,
  badgeDetails,
  isMobile,
  copied,
  setCopied,
  setLoading
}) => {
  if (active === 'linkedin' || active === 'forward') {
    const link = badgeDetails.url;
    return (
      <DarkPopup
        showPopup={true}
        onOverlayClick={() => changeActiveTab('none')}
        style={{
          zIndex: '1050'
        }}
        popupClassName={c('infoPopup', active === 'linkedin' && 'fullheight')}
      >
        {active === 'linkedin'
          ? renderLinkedinTab(badgeDetails, isMobile, changeActiveTab)
          : renderShareTab({ link, isMobile, changeActiveTab, copied, setCopied })}
      </DarkPopup>
    );
  }

  if (active === 'download') {
    setLoading(true);
    fetch(badgeDetails.imageUrl, {
      mode: 'no-cors',
      headers: {
        'Access-Control-Allow-Origin': '*'
      }
    })
      .then(response => {
        return response.blob();
      })
      .then(blob => {
        return saveAs(blob, badgeDetails.imageUrl.split('/').pop());
      })
      .then(() => {
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        console.log('Badge Download Error', error);
        showSnackbar('Error while downloading badge, please try again. ' + error);
      });
    changeActiveTab('none');
    return null;
  }
  return null;
};

const renderBadgeActions = ({
  renderActions,
  isMobile,
  badgeDetails,
  showShareOptions,
  toggleShareOptions,
  active,
  changeActiveTab,
  copied,
  setCopied,
  loading,
  setLoading
}) => {
  if (!renderActions) {
    return null;
  }
  const tabs = [
    {
      text: 'Add to LinkedIn',
      onClick: event => {
        event.stopPropagation();
        changeActiveTab('linkedin');
      },
      name: 'linkedin',
      icon: 'linkedin'
    },
    {
      text: 'Share Badge',
      onClick: event => {
        event.stopPropagation();
        changeActiveTab('forward');
      },
      name: 'upload',
      icon: 'upload'
    },
    {
      text: 'Download Badge',
      onClick: event => {
        event.stopPropagation();
        changeActiveTab('download');
      },
      name: 'download',
      icon: 'download',
      disabled: loading
    }
  ];
  const white = themeConfig.color.dark.white;
  if (isMobile) {
    return (
      <React.Fragment>
        {!showShareOptions ? (
          <DarkButton.SecondaryButton
            className="shareButton"
            onClick={() => toggleShareOptions(true)}
            size="medium"
            uppercase={true}
          >
            <Icon type="share" className="icon" />
            Share Badge
          </DarkButton.SecondaryButton>
        ) : (
          <AnimateSliderBottom
            closeSlider={() => toggleShareOptions(false)}
            className="animateSlider"
          >
            <div className="optionsWrapper">
              <DarkText
                className="header"
                as="div"
                letterSpacing={0.5}
                fontSize={12}
                uppercase={true}
                color="white"
              >
                Share Badge
              </DarkText>
              {tabs.map(tab => (
                <DarkText
                  className={c('option', tab.disabled && 'disabled')}
                  as="div"
                  letterSpacing="normal"
                  fontSize={14}
                  onClick={event => tab.onClick(event)}
                >
                  {tab.disabled && loading ? (
                    <Loader
                      style={{ marginRight: '8px' }}
                      svgColor={white}
                      width={16}
                      height={16}
                    />
                  ) : (
                    <Icon
                      type={tab.icon}
                      style={{
                        width: 16,
                        marginRight: '8px',
                        fill: white
                      }}
                    />
                  )}
                  {tab.text}
                </DarkText>
              ))}
            </div>
          </AnimateSliderBottom>
        )}
        {renderOpenTab({
          active,
          changeActiveTab,
          badgeDetails,
          isMobile,
          copied,
          setCopied,
          setLoading
        })}
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <div className="tabWrapperContainer">
        <div className="tabWrapper">
          {tabs.map(tab => {
            const iconSize = tab.name === 'linkedin' ? '23px' : '16px';
            return (
              <div className="tab">
                <DarkButton.TextButton onClick={tab.onClick} disabled={tab.disabled}>
                  <div className="wrapper">
                    {tab.disabled && loading ? (
                      <Loader
                        style={{ marginRight: '7px', marginTop: '6px' }}
                        svgColor={white}
                        width={16}
                        height={16}
                      />
                    ) : (
                      <Icon
                        type={tab.name}
                        style={{
                          width: iconSize,
                          height: iconSize,
                          marginRight: '7px',
                          marginTop: '2px',
                          fill: white
                        }}
                      />
                    )}

                    <DarkText
                      className="buttonText"
                      color="white"
                      fontSize={12}
                      as="div"
                      letterSpacing={0.5}
                    >
                      {tab.text}
                    </DarkText>
                  </div>
                </DarkButton.TextButton>
              </div>
            );
          })}
        </div>
      </div>

      {renderOpenTab({
        active,
        changeActiveTab,
        badgeDetails,
        isMobile,
        copied,
        setCopied,
        setLoading
      })}
    </React.Fragment>
  );
};

export const BadgeView = ({ badge, show, onClose, isMobile, renderActions = true }) => {
  const [badgeDetails, setBadgeDetails] = useState(null);
  const [showShareOptions, toggleShareOptions] = useState(false);
  const [active, changeActiveTab] = useState('none');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async issueId => {
      const badgeId = issueId || `${badge.userId}_${badge.contentId}`;
      const badgeData = await fetchBadge(badgeId);
      if (!badgeData.error) {
        const { response } = badgeData;
        setBadgeDetails({
          badgeName: response.badge.name,
          imageUrl: response.badge.url,
          pid: response.badge.pid,
          company: response.companyName,
          from: moment(response.completedOn).format('MMM YYYY'),
          fromUnformatted: response.completedOn,
          to: 'This badge doesn’t expire.',
          activityTitle: response.contentName,
          activityType: response.contentType,
          userName: response.userName,
          url: window._currentUserInfo
            ? `http://${window._currentUserInfo.company_subdomain}.${window.location.host.split(
                '.'
              )[1] || 'lyearn'}.com/share.html?type=badge&issueId=${badgeId}`
            : `http://${response.companyName}.${window.location.host.split('.')[1] ||
                'lyearn'}.com/share.html?type=badge&issueId=${badgeId}`,
          number: badgeId
        });
        setLoading(false);
      } else if (badgeData.error) {
        let label = '';
        switch (badgeData.code) {
          case '311':
            label = 'Badge issueId is missing, please enter correct URL.';
            break;
          case '312':
            label = 'Badge issueId is not valid, please enter correct URL.';
            break;
          case '313':
            label = "This content doesn't have any badge attached, please contact support team!";
            break;
          case '314':
            label = 'Badge is not available yet as user has not completed this content.';
            break;
          default:
            label = 'Error fetching badge, please try again.';
        }
        setLoading(false);
        return showSnackbar(label);
      }
    };
    if (show && badge && (badge.pid || badge.issueId || (badge.userId && badge.contentId))) {
      fetchData(badge.issueId);
    }
  }, [badge, show]);

  if (!badge || badge === 'null' || (!badge.pid && !badge.issueId)) {
    return null;
  }

  if (!show) {
    return null;
  }

  if (!badgeDetails && loading) {
    return (
      <div className={previewContainer(true, isMobile)}>
        <ActivityIndicator />
      </div>
    );
  }

  if (!badgeDetails) {
    return null;
  }

  return (
    <div
      className={previewContainer(false, isMobile)}
      onClick={event => event.stopPropagation()}
      role="button"
      tabIndex="0"
    >
      <div className="headerRoot">
        <DarkButton.IconButton onClick={onClose}>
          <Icon type="arrow-left" className="icon" />
        </DarkButton.IconButton>
      </div>
      <div className="badgeInfo">
        <DarkText
          className="completion"
          fontSize={isMobile ? 12 : 14}
          letterSpacing={2}
          color="copper"
          uppercase={true}
          as="div"
        >
          Badge of Completion
        </DarkText>
        <DarkText
          className="congratulations"
          fontSize={isMobile ? 24 : 44}
          letterSpacing="normal"
          color="silver"
          as="div"
        >
          {renderActions
            ? `Congratulations, ${badgeDetails.userName.split(' ')[0]}! 🎉`
            : badgeDetails.userName}
        </DarkText>
        <DarkText
          className="contentInfo"
          fontSize={isMobile ? 12 : 14}
          letterSpacing="normal"
          color="iron"
          as="div"
        >
          {renderActions
            ? 'You have been awarded a badge for completing'
            : 'Awarded a badge for successfully completing'}
          <span className="contentName"> {badgeDetails.activityTitle} </span>
          {badgeDetails.activityType === 'liveTraining'
            ? 'live training'
            : badgeDetails.activityType}
        </DarkText>
      </div>
      <div className="badgeImageWrapper">
        <img className="badgeImage" src={badgeDetails.imageUrl} alt={badgeDetails.badgeName} />
      </div>
      {renderBadgeActions({
        renderActions,
        isMobile,
        badgeDetails,
        showShareOptions,
        toggleShareOptions,
        active,
        changeActiveTab,
        copied,
        setCopied,
        loading,
        setLoading
      })}
    </div>
  );
};
